import Vue from 'vue'
import Vuex from 'vuex'
import docendo from '../docendo'
import eventbus from '../eventbus'
import subs from './modules/subs'
import subjects from './modules/subjects'
import employments from './modules/employments'
import flipper from './modules/flipper'
import calendar from './modules/calendar'
import analytics from './modules/analytics'
import danishHolidays from './modules/danishHolidays'
import {
  isAssignedPoolFromNormWhenPoolsDeactivated,
  schoolCanConfigureAssignedPoolNormWhenPoolsDeactivated
} from '../utils/assignedPoolWhenPoolsDeactivated'

Vue.use(Vuex)

const state = {
  user: null,
  isDeviceMobile: false,
  isUserLoaded: false,
  userSettings: null,
  schoolSettings: {},
  isLoadingSchoolSettings: false,
  activeSchool: window.SCHOOL ? window.SCHOOL : null,
  allUsers: [],
  isLoadingAllUsers: true,
  calendars: [],
  isLoadingSchoolCalenders: true,
  schoolYears: [],
  activeSchoolYears: [],
  noActiveSchoolYears: false,
  activatedSchoolYear: '',
  localActivatedSchoolYear: '',
  onboardingStep: 0,
  selectedTags: [],
  calendarRelations: [],
  isLoadingCalendarRelations: true,
  isUpdatingCalendars: false
}

const getters = {
  locale: (state) => {
    return state.user && state.user.locale ? state.user.locale : window.USER ? window.USER.language : 'en'
  },
  schoolId: (state) => {
    return state.activeSchool ? state.activeSchool.id * 1 : window.SCHOOL && window.SCHOOL.id ? window.SCHOOL.id * 1 : null
  },
  schoolYearId: (state) => {
    return state.localActivatedSchoolYear ? state.localActivatedSchoolYear.id : null
  },
  schoolCountry: (state) => {
    return state.activeSchool && state.activeSchool.country ? state.activeSchool.country : window.SCHOOL && window.SCHOOL.country ? window.SCHOOL.country : 'DK'
  },
  isSchoolSystem: (state) => {
    const school = state.activeSchool && state.activeSchool.state ? state.activeSchool : window.SCHOOL
    return !!school && school.state === 'system'
  },
  schoolFeatures: (state, getters, rootState) => {
    return state.activeSchool && state.activeSchool.features ? state.activeSchool.features : window.SCHOOL && window.SCHOOL.features ? window.SCHOOL.features : []
  },
  salarySystem () {
    return state.activeSchool && state.activeSchool.salary_system ? state.activeSchool.salary_system : null
  },
  isArbeidstidsplanActivated: (state) => {
    const features = state.activeSchool && state.activeSchool.features ? state.activeSchool.features : window.SCHOOL && window.SCHOOL.features ? window.SCHOOL.features : []
    return features.indexOf('work_time') > -1
  },
  firstDayOfWeek: (state) => {
    return state.activeSchool && state.activeSchool.first_day_of_week_num !== undefined ? state.activeSchool.first_day_of_week_num : 1
  },
  userSchools: (state, getters, rootState, rootGetters) => {
    let schools = state.user && state.user.memberships ? state.user.memberships : []
    return schools.reduce((obj, item) => {
      obj[item.school_id] = item
      return obj
    }, {})
  },
  isPermissionsActivatedForSchool: state => {
    if (!state.user) {
      return true
    }
    if (state.user && state.user.admin) {
      return false
    }
    if (state.user && state.user.memberships) {
      return state.user.current_school.authorize
    }
  },
  userPermissions: state => {
    if (state.user && state.user.memberships) {
      let idx = state.user.memberships.findIndex(school => school.school_id === state.activeSchool.id)
      if (idx > -1) {
        return state.user.memberships[idx].permissions
      } else {
        return []
      }
    } else {
      return []
    }
  },
  usersSortedOnName: (state) => {
    const users = state.allUsers.filter(user => user.name !== null && user.name !== '')
    return Vue.prototype.$helpers.sortArrOnName(users)
  },
  usersFilteredWithTags: (state, getters) => {
    if (state.selectedTags.length < 1) {
      return getters.usersSortedOnName
    }
    return getters.usersSortedOnName.filter(user => user.user_tags.some(tag => state.selectedTags.indexOf(tag) >= 0))
  },
  allCalendarsInHash: (state, getters, rootState, rootGetters) => {
    let cals = state.calendars
    return cals.reduce((obj, item) => {
      obj[item.id] = item
      return obj
    }, {})
  },
  klassCalendars: (state, getters) => {
    const klasses = state.calendars.filter(cal => cal.type === 'klass' && cal.name !== null && cal.name !== '')
    const klassesInActiveYear = klasses.filter(cal => cal.school_year_id ? cal.school_year_id === state.localActivatedSchoolYear.id : cal.year === state.localActivatedSchoolYear.name)
    return Vue.prototype.$helpers.sortArrOnName(klassesInActiveYear)
  },
  userCalendars: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'user' && cal.name !== null && cal.name !== '')
    return Vue.prototype.$helpers.sortArrOnName(cals)
  },
  roomCalendars: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'room' && !cal.archived_at && cal.name !== null && cal.name !== '')
    return Vue.prototype.$helpers.sortArrOnName(cals)
  },
  roomCalendarsInclArchived: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'room' && cal.name !== null && cal.name !== '')
    return Vue.prototype.$helpers.sortArrOnName(cals)
  },
  mergeCalendars: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'merge' && cal.name !== '' && !cal.archived_at)
    const calsInActiveYear = cals.filter(cal => !cal.school_year_id || cal.school_year_id === state.localActivatedSchoolYear.id)
    return Vue.prototype.$helpers.sortArrOnName(calsInActiveYear)
  },
  mergeCalendarsInclArchived: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'merge')
    return Vue.prototype.$helpers.sortArrOnName(cals)
  },
  otherCalendars: (state, getters) => {
    const otherCals = state.calendars.filter(cal => cal.type === 'other' && cal.name !== '')
    const otherCalsInActiveYear = otherCals.filter(cal => cal.school_year_id ? cal.school_year_id === state.localActivatedSchoolYear.id : cal.year === state.localActivatedSchoolYear.name)
    return Vue.prototype.$helpers.sortArrOnName(otherCalsInActiveYear)
  },
  clubCalendars: (state, getters) => {
    const clubCals = state.calendars.filter(cal => cal.type === 'club' && cal.name !== '')
    const clubCalsInActiveYear = clubCals.filter(cal => cal.school_year_id ? cal.school_year_id === state.localActivatedSchoolYear.id : cal.year === state.localActivatedSchoolYear.name)
    return Vue.prototype.$helpers.sortArrOnName(clubCalsInActiveYear)
  },
  allStudentCalendars: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'student' && cal.name !== '')
    return Vue.prototype.$helpers.sortArrOnName(cals)
  },
  studentCalendars: (state, getters) => {
    return getters.allStudentCalendars.filter(cal => !cal.archived_at)
  },
  kindergartenCalendars: (state, getters) => {
    const cals = state.calendars.filter(cal => cal.type === 'kindergarten' && cal.name !== '')
    const calsInActiveYear = cals.filter(cal => cal.school_year_id ? cal.school_year_id === state.localActivatedSchoolYear.id : cal.year === state.localActivatedSchoolYear.name)
    return Vue.prototype.$helpers.sortArrOnName(calsInActiveYear)
  },
  allKlassCalendarsInHash: (state, getters) => {
    let cals = state.calendars.filter(cal => cal.type === 'klass')
    let newCals = cals.reduce((obj, item) => {
      obj[item.id] = item
      return obj
    }, {})
    return newCals
  },
  allUsersInCalIdHash: (state, getters) => state.allUsers.reduce((obj, item) => {
    obj[item.user_calendar_id] = item
    return obj
  }, {}),
  // FOR ABSENT USERS WITHOUT ABSENCE
  allUsersInHash: state => state.allUsers.reduce((obj, item) => {
    obj[item.id] = item
    return obj
  }, {}),
  hiddenTaskDistributionCategories: state => {
    return state.schoolSettings && state.schoolSettings.hiddenTaskDistributionCategories ? state.schoolSettings.hiddenTaskDistributionCategories : []
  },
  shownOwnPools: state => {
    return state.schoolSettings && state.schoolSettings.shownOwnPools ? state.schoolSettings.shownOwnPools : []
  },
  canConfigureAssignedPoolNormWhenPoolsDeactivated: (state, getters) => {
    return schoolCanConfigureAssignedPoolNormWhenPoolsDeactivated(getters.schoolId)
  },
  isAssignedPoolFromNormWhenPoolsDeactivated: state => {
    return isAssignedPoolFromNormWhenPoolsDeactivated(state.schoolSettings)
  },
  localPoolTitle: state => {
    if (!state.schoolSettings || !state.schoolSettings.localPoolTitle) {
      return null
    }
    if (typeof state.schoolSettings.localPoolTitle !== 'string') {
      return state.schoolSettings.localPoolTitle
    }
    const trimmed = state.schoolSettings.localPoolTitle.trim()
    return trimmed.length > 0 ? trimmed : null
  },
  standardSubjectCodes: () => {
    return [
      {subject_code: 110, fagkode: '110', name: 'Dansk'},
      {subject_code: 120, fagkode: '120', name: 'Engelsk'},
      {subject_code: 130, fagkode: '130', name: 'Tysk (tilbudsfag)'},
      {subject_code: 134, fagkode: '134', name: 'Tysk (valgfag)'},
      {subject_code: 140, fagkode: '140', name: 'Fransk (tilbudsfag)'},
      {subject_code: 144, fagkode: '144', name: 'Fransk (valgfag)'},
      {subject_code: 170, fagkode: '170', name: 'Kristendomskundskab'},
      {subject_code: 171, fagkode: '171', name: 'Kristendomskundskab/religion'},
      {subject_code: 181, fagkode: '181', name: 'Historie'},
      {subject_code: 182, fagkode: '182', name: 'Samfundsfag'},
      {subject_code: 210, fagkode: '210', name: 'Idræt'},
      {subject_code: 220, fagkode: '220', name: 'Musik'},
      {subject_code: 230, fagkode: '230', name: 'Billedkunst'},
      {subject_code: 240, fagkode: '240', name: 'Håndarbejde'},
      {subject_code: 245, fagkode: '245', name: 'Håndværk og design'},
      {subject_code: 250, fagkode: '250', name: 'Sløjd'},
      {subject_code: 260, fagkode: '260', name: 'Madkundskab'},
      {subject_code: 310, fagkode: '310', name: 'Matematik'},
      {subject_code: 320, fagkode: '320', name: 'Fysik/kemi'},
      {subject_code: 330, fagkode: '330', name: 'Geografi'},
      {subject_code: 340, fagkode: '340', name: 'Biologi'},
      {subject_code: 350, fagkode: '350', name: 'Natur/teknik'},
      {subject_code: 511, fagkode: '511', name: 'Praktisk/Musisk valgfag: Håndværk/design'},
      {subject_code: 520, fagkode: '520', name: 'Praktisk/Musisk valgfag: Musik'},
      {subject_code: 531, fagkode: '531', name: 'Praktisk/Musisk valgfag: Billedkunst'},
      {subject_code: 540, fagkode: '540', name: 'Praktisk/Musisk valgfag: Madkundskab'},
      {subject_code: 600, fagkode: '600', name: 'Børnehaveklasse'}
    ]
  },
  userTagsWithoutPrepFactor (state, getters) {
    return state.schoolSettings.pedagogicalGroups ? state.schoolSettings.pedagogicalGroups : []
  },
  userTagsForSumWorkHours (state) {
    return state.schoolSettings.workHoursSumGroups ? state.schoolSettings.workHoursSumGroups : []
  }
}

const mutations = {
  setIsUserLoaded: (state, boolean) => {
    state.isUserLoaded = boolean
  },
  setSelectedTags: (state, tag) => {
    if (state.selectedTags.indexOf(tag) < 0) {
      state.selectedTags.push(tag)
    } else {
      state.selectedTags.splice(state.selectedTags.indexOf(tag), 1)
    }
  },
  setUser: (state, data) => {
    state.user = data
  },
  updateUserProp: (state, data) => {
    const prop = data.prop
    state.user[prop] = data[prop]
  },
  setIsDeviceMobile: (state, boolean) => {
    state.isDeviceMobile = boolean
  },
  setUserSettings: (state, data) => {
    state.userSettings = JSON.parse(data)
  },
  setSchoolSettings: (state, data) => {
    let parsedData
    try {
      parsedData = data && data.settings ? data.settings : data
    } catch (err) {
      parsedData = data
      console.error(err.message, err)
    }
    state.schoolSettings = parsedData
  },
  updateSchoolUser: (state, data) => {
    const idx = state.allUsers.findIndex(user => user.id === data.id)
    Vue.set(state.allUsers, idx, data)
  },
  updatePropOnSchoolUser: (state, data) => {
    const prop = data.prop
    const idx = state.allUsers.findIndex(user => user.id === data.userId)
    state.allUsers[idx][prop] = data[prop]
  },
  setActiveSchool: (state, data) => {
    state.activeSchool = data
  },
  updateActiveSchoolDuration: (state, subjectDefaultDuration) => {
    state.activeSchool.subject_default_duration = subjectDefaultDuration
  },
  setCalendars: (state, data) => {
    state.calendars = data
  },
  updateCalendars: (state, calendars) => {
    calendars.forEach(calendar => {
      let idx = state.calendars.findIndex(cal => cal.id === calendar.id)
      Vue.set(state.calendars, idx, calendar)
    })
  },
  updatePropsOnCalendars: (state, updatedCalendars) => {
    // Only update props because student calendars are  mutated with calendar relations
    const props = Object.keys(updatedCalendars[0]) // [calendar_id, anonymous_access, etc.]
    updatedCalendars.forEach(calendar => {
      const idx = state.calendars.findIndex(cal => cal.id === calendar.calendar_id)
      let updatedCalendar = state.calendars[idx]
      props.forEach(propName => {
        if (propName !== 'calendar_id') {
          updatedCalendar[propName] = calendar[propName]
        }
      })
      Vue.set(state.calendars, idx, updatedCalendar)
    })
  },
  setIsLoadingSchoolCalenders: (state, boolean) => {
    state.isLoadingSchoolCalenders = boolean
  },
  setUsers: (state, data) => {
    state.allUsers = data
  },
  setIsLoadingAllUsers: (state, boolean) => {
    state.isLoadingAllUsers = boolean
  },
  setOnboardingStep: (state, num) => {
    state.onboardingStep = num
  },
  setSchoolYears: (state, data) => {
    state.schoolYears = data
    state.activeSchoolYears = data.filter(year => year.active)
    if (state.activeSchoolYears.length === 0) {
      state.noActiveSchoolYears = true
    } else {
      state.noActiveSchoolYears = false
    }
  },
  setlocalActivatedSchoolYear: (state, schoolYearId) => {
    const idx = state.activeSchoolYears.map(e => e.id).indexOf(schoolYearId * 1)
    let activeYearId = ''
    if (idx > -1) {
      state.localActivatedSchoolYear = state.activeSchoolYears[idx]
      activeYearId = schoolYearId
    } else {
      state.localActivatedSchoolYear = state.activeSchoolYears[0]
      activeYearId = state.activeSchoolYears[0].id
    }
    try {
      if (typeof (Storage) !== 'undefined') {
        window.localStorage.setItem('doActiveSchoolYearId', activeYearId)
      }
    } catch (e) {
      // localstorage probably not enabled
    }
  },
  updateSchoolCalendar (state, updateObj) {
    let idx = state.calendars.findIndex(calendar => calendar.id === updateObj.id)
    let updatedCalendar = state.calendars[idx]
    // Hack because received lesson object is different structure from original, so only take prop
    updatedCalendar.name = updateObj.name
    updatedCalendar.archived_at = updateObj.archived_at
    updatedCalendar.custom_data = updateObj.custom_data
    Vue.set(state.calendars, idx, updatedCalendar)
  },
  deleteSchoolCalendar (state, calId) {
    const idx = state.calendars.findIndex(calendar => calendar.id === calId)
    state.calendars.splice(idx, 1)
  },
  setRelations (state, data) {
    state.calendarRelations = data
  },
  updateRelations (state, data) {
    const idx = state.calendarRelations.findIndex(relations => relations.id === data.id)
    Vue.set(state.calendarRelations, idx, data)
  },
  addRelation (state, data) {
    state.calendarRelations.push(data)
  },
  deleteRelation (state, data) {
    const idx = state.calendarRelations.findIndex(relations => relations.id === data.id)
    state.calendarRelations.splice(idx, 1)
  }
}

const actions = {
  // this.$store.dispatch('trackEvent', 'substitution_altTrigger_used') <område>_<feature>_<handling>
  trackEvent: (context, eventName) => {
    const schoolId = context.rootGetters.schoolId
    docendo.trackEvent(schoolId, eventName, success => {
    }, failure => {
      console.log(failure)
    })
  },
  getUser: context => {
    context.commit('setIsUserLoaded', false)
    docendo.getUser(data => {
      if (data.status === 'error') {
        const pathUrl = window.location.href.replace(window.location.origin, '')
        window.location.replace(data.redirect_to + '?return_to=' + encodeURIComponent(pathUrl))
        return
      }
      context.commit('setIsUserLoaded', true)
      context.commit('setUser', data)
      context.commit('setUserSettings', data.settings)
    })
  },
  readSchoolSettings: (context) => {
    const schoolId = context.state.activeSchool.id
    context.state.isLoadingSchoolSettings = true
    docendo.showSchool(schoolId, data => {
      context.commit('setSchoolSettings', data.custom_data)
      context.state.isLoadingSchoolSettings = false
    }, failure => {
      console.log('failure')
    })
  },
  readActiveSchool: (context, school) => {
    const schoolId = school.id
    docendo.showSchool(schoolId, data => {
      context.commit('setActiveSchool', data)
      context.commit('setSchoolSettings', data.custom_data)
    }, failure => {
      console.log('failure')
    })
  },
  loadActiveSchool: (context, school) => {
    const schoolId = school.id
    context.commit('setActiveSchool', school)
    context.dispatch('getSchoolCalendars', schoolId)
    context.dispatch('readSchoolYears', schoolId)
    context.dispatch('getUsersFromSchoolId', schoolId)
  },
  // this.$store.dispatch('updateUserSettings', {'setupWidgetOpen': true})
  updateUserSettings (context, obj) {
    const str = context.state.userSettings || {}
    const prop = Object.keys(obj)[0]
    const val = obj[prop]
    str[prop] = val
    docendo.updateUser({settings: JSON.stringify(str)},
      success => context.commit('setUserSettings', success.settings)
    )
  },
  // this.$store.dispatch('deleteUserSetting', 'setupWidgetOpen')
  deleteUserSetting (context, prop) {
    const str = context.state.userSettings
    delete str[prop]
    docendo.updateUser({settings: JSON.stringify(str)},
      success => {
        context.commit('setUserSettings', success.settings)
        eventbus.toast = {text: window.use_i18n.t('vue2.common.saved'), 'success': true}
      }
    )
  },
  // Single key: this.$store.dispatch('updateSchoolSettings', {'setupWidgetOpen': true})
  // Multi key:  this.$store.dispatch('updateSchoolSettings', {'a': 1, 'b': [2]})
  // En værdi på null/undefined fjerner nøglen (atomært i samme save).
  updateSchoolSettings (context, obj) {
    const schoolId = context.state.activeSchool.id
    const settingsObj = {...(context.state.schoolSettings || {})}

    Object.keys(obj).forEach(prop => {
      const val = obj[prop]
      if (val === null || val === undefined) {
        if (settingsObj.hasOwnProperty(prop)) {
          delete settingsObj[prop]
        }
      } else {
        settingsObj[prop] = Array.isArray(val) ? val.slice() : val
      }
    })

    docendo.updateSchool(
      schoolId,
      {custom_data: {settings: settingsObj}},
      success => {
        context.commit('setSchoolSettings', success.custom_data)
        eventbus.toast = {text: window.use_i18n.t('vue2.common.saved'), 'success': true}
      },
      failure => {
        console.log({failure})
      }
    )
  },
  // this.$store.dispatch('updateSchoolSettings', {'setupWidgetOpen': true})
  deleteSchoolSetting (context, prop) {
    const schoolId = context.state.activeSchool.id
    const settingsObj = {...(context.state.schoolSettings || {})}

    if (settingsObj.hasOwnProperty(prop)) {
      delete settingsObj[prop]
    }
    docendo.updateSchool(
      schoolId,
      {custom_data: {settings: settingsObj}},
      success => {
        context.commit('setSchoolSettings', success.custom_data)
        eventbus.toast = {text: window.use_i18n.t('vue2.common.saved'), success: true}
      },
      failure => {
        console.log({failure})
      }
    )
  },
  updateSchoolSettingsForSchoolYear (context, saveObj) {
    const schoolId = context.state.activeSchool.id
    const schoolYearId = context.state.localActivatedSchoolYear.id
    const settingsObj = context.state.schoolSettings
    const prop = Object.keys(saveObj)[0]
    const val = saveObj[prop]
    const dataProp = 'school_year_' + schoolYearId
    if (settingsObj.hasOwnProperty(dataProp)) {
      settingsObj[dataProp][prop] = val
    } else {
      settingsObj[dataProp] = {[prop]: val}
    }
    // settings: {testing: 1234, school_year_14441: {test: 12314, arr: [1, 2, 3]}}
    docendo.updateSchool(schoolId, {custom_data: {settings: settingsObj}},
      success => context.commit('setSchoolSettings', success.custom_data)
    )
  },
  deleteSchoolSettingForSchoolYear (context, prop) {
    const schoolId = context.state.activeSchool.id
    const schoolYearId = context.state.localActivatedSchoolYear.id
    const settingsObj = context.state.schoolSettings
    const dataProp = 'school_year_' + schoolYearId
    if (settingsObj[dataProp] && settingsObj[dataProp][prop]) {
      delete settingsObj[dataProp][prop]
      if (Object.keys(settingsObj[dataProp]).length === 0) {
        delete settingsObj[dataProp]
      }
      docendo.updateSchool(schoolId, {custom_data: {settings: settingsObj}},
        success => context.commit('setSchoolSettings', success.custom_data)
      )
    }
  },
  getSchoolCalendars: (context, id) => {
    context.commit('setIsLoadingSchoolCalenders', true)
    docendo.calendarsForSchool(id, data => {
      context.commit('setCalendars', data)
      context.commit('setIsLoadingSchoolCalenders', false)
      context.dispatch('setCalendarRelations')
    })
  },
  updateSchoolCalendars (context, calendars) {
    context.state.isUpdatingCalendars = true
    docendo.schoolBulkUpdateCalendars(window.SCHOOL.id, calendars, success => {
      context.commit('updatePropsOnCalendars', calendars)
      context.state.isUpdatingCalendars = false
    }, failure => {
      console.log('failure', failure)
    })
  },
  getUsers: (context, schoolId, userIdsFilter) => {
    context.commit('setIsLoadingAllUsers', true)
    docendo.schoolUsers(window.SCHOOL.id, userIdsFilter, (data) => {
      context.commit('setIsLoadingAllUsers', false)
      if (data.status) {
        return
      }
      context.commit('setUsers', data)
    })
  },
  getUsersFromSchoolId: (context, schoolId) => {
    context.commit('setIsLoadingAllUsers', true)
    docendo.schoolUsers2(schoolId, data => {
      context.commit('setIsLoadingAllUsers', false)
      if (data.status) {
        return
      }
      context.commit('setUsers', data)
    })
  },
  readSchoolYears: (context, schoolId) => {
    let activeSchoolId = null
    if (schoolId) {
      activeSchoolId = schoolId
    } else {
      activeSchoolId = window.SCHOOL.id
    }
    docendo.schoolYearsRead(activeSchoolId, '', 'id', data => {
      context.commit('setSchoolYears', data)
      try {
        if (typeof (Storage) !== 'undefined') {
          if (window.localStorage.getItem('doActiveSchoolYearId') !== null) {
            context.commit('setlocalActivatedSchoolYear', window.localStorage.getItem('doActiveSchoolYearId'))
          } else {
            let activeYears = context.state.activeSchoolYears
            context.commit('setlocalActivatedSchoolYear', activeYears[0].id)
          }
        }
      } catch (e) {
        // localstorage probably not enabled
      }
    })
  },
  updateCalendar (context, calObj) {
    docendo.schoolCalendarsUpdate(window.SCHOOL.id, calObj.calendarId, calObj.changes, success => {
      let updateObj = {
        id: success.id,
        name: success.name,
        archived_at: success.archived_at,
        custom_data: {}
      }
      if (success.custom_data) {
        updateObj['custom_data'] = success.custom_data
      }
      context.commit('updateSchoolCalendar', updateObj)
      eventbus.toast = {text: 'Calendar updated', 'success': true}
    }, failure => {
      console.log('failure', failure)
    })
  },
  deleteCalendar (context, calId) {
    eventbus.toast = {text: 'Deleting calendar', 'success': false}
    docendo.schoolCalendarsDelete(window.SCHOOL.id, calId, success => {
      context.commit('deleteSchoolCalendar', calId)
      eventbus.toast = {text: 'Calendar deleted', 'success': true}
    }, failure => {
      console.log('failure', failure)
    })
  },
  readCalendarRelations: (context) => {
    context.state.isLoadingCalendarRelations = true
    docendo.indexCalendarRelations(window.SCHOOL.id, data => {
      context.commit('setRelations', data)
      context.state.isLoadingCalendarRelations = false
      context.dispatch('setCalendarRelations')
    }, failure => {
      console.log('failed read relations')
    })
  },
  createRelation: (context, obj) => {
    docendo.createCalendarRelation(window.SCHOOL.id, obj.calendarId, obj.otherCalendarId, obj.relation, data => {
      context.commit('addRelation', data)
      context.dispatch('setCalendarRelations')
    }, failure => {
      console.log('failed to set relation')
    })
  },
  deleteRelation: (context, relationId) => {
    docendo.deleteCalendarRelation(window.SCHOOL.id, relationId, data => {
      context.commit('deleteRelation', data)
      context.dispatch('setCalendarRelations')
    }, failure => {
      console.log('failed to set relation')
    })
  },
  setCalendarRelations: (context) => {
    if (context.state.isLoadingSchoolCalenders || context.state.isLoadingCalendarRelations) {
      return
    }
    context.state.calendars.forEach(
      calendar => {
        if (calendar.type === 'klass') {
          let students = []
          context.state.calendarRelations.forEach(relation => {
            if (relation.other_calendar_id === calendar.id) {
              const idx = students.indexOf(relation.other_calendar_id)
              if (idx < 0) {
                students.push(relation.calendar_id)
              }
            }
          })
          Vue.set(calendar, 'students', students)
        }
        if (calendar.type === 'student') {
          let calRelations = []
          let klasses = []
          context.state.calendarRelations.forEach(relation => {
            if (relation.calendar_id === calendar.id) {
              let idx = klasses.indexOf(relation.calendar_id)
              if (idx < 0) {
                calRelations.push(relation)
                klasses.push(relation.other_calendar_id)
              }
            }
          })
          Vue.set(calendar, 'klasses', klasses)
          Vue.set(calendar, 'calRelations', calRelations)
        }
      }
    )
  }
}
export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions,
  modules: {
    subs,
    subjects,
    employments,
    flipper,
    calendar,
    analytics,
    danishHolidays
  }
})



// WEBPACK FOOTER //
// ./src/store/index.js